import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Bar, Doughnut } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import SegmentChartCardKebabMenu from "./SegmentChartCardKebabMenu";
import { DashboardChartTableSortHeader } from "./DashboardChartTableSortHeader";
import { customerTierMedalUrl, mergeCustomerGroupModel } from "./dashboardChartsModel";
import { buildTimestampedPngFilename, downloadElementAsPng } from "./downloadCapturePng";
import "./dashboardChartJsSetup";

function formatCount(n) {
  const v = typeof n === "number" ? n : Number(n);
  if (!Number.isFinite(v)) return "";
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(Math.round(v));
}

/**
 * Tier medal image (Gold / Silver / Platinum).
 * @param {{ tierId: string, label: string }} props
 */
function TierMedal({ tierId, label }) {
  const k = String(tierId).toLowerCase();
  const alt = label || k;
  return (
    <img
      className="pos-customer-group__tier-medal"
      src={customerTierMedalUrl(k)}
      alt={alt}
      draggable={false}
    />
  );
}

/**
 * @param {{ model: import("./dashboardChartsModel.js").CustomerGroupModel }} props
 */
function CustomerGroupFooterLegend({ model }) {
  const rows = useMemo(() => {
    const tiers = model?.tiers ?? [];
    let total = 0;
    for (const t of tiers) {
      const c = Number(t.count);
      total += Number.isFinite(c) ? c : 0;
    }
    return tiers.map((t, i) => {
      const c = Number(t.count);
      const count = Number.isFinite(c) ? c : 0;
      const pct = total > 0 ? Math.round((count / total) * 100) : 0;
      return {
        key: `cg-leg-${i}-${t.id}`,
        tierId: t.id,
        legendLabel: t.legendLabel,
        count,
        pct,
      };
    });
  }, [model]);

  return (
    <div className="pos-customer-group-legend" role="list">
      {rows.map((row) => (
        <div key={row.key} className="pos-customer-group-legend__row" role="listitem">
          <div className="pos-customer-group-legend__left">
            <TierMedal tierId={row.tierId} label={row.legendLabel} />
            <span className="pos-customer-group-legend__name">{row.legendLabel}</span>
          </div>
          <span className="pos-customer-group-legend__count">{formatCount(row.count)}</span>
          <span className="pos-customer-group-legend__pct" style={{fontSize:'10px',color:'#A4A4A4'}}>({row.pct}%)</span>
        </div>
      ))}
    </div>
  );
}

/**
 * @param {{ model: import("./dashboardChartsModel.js").CustomerGroupModel }} props
 */
function CustomerGroupDonutBody({ model }) {
  const { t } = useTranslation();
  const { labels, values, colors, total } = useMemo(() => {
    const tiers = model?.tiers ?? [];
    const labelsN = [];
    const valuesN = [];
    const colorsN = [];
    let tot = 0;
    for (const tier of tiers) {
      labelsN.push(tier.legendLabel ?? "");
      const v = Number(tier.count);
      const vv = Number.isFinite(v) ? v : 0;
      valuesN.push(vv);
      tot += vv;
      colorsN.push(tier.color ?? "#F9C54A");
    }
    return { labels: labelsN, values: valuesN, colors: colorsN, total: tot };
  }, [model]);

  const chartData = useMemo(
    () => ({
      labels,
      datasets: [
        {
          data: values,
          backgroundColor: colors,
          borderColor: "#FFFFFF",
          borderWidth: 1,
        },
      ],
    }),
    [labels, values, colors],
  );

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      cutout: "68%",
      interaction: { mode: "nearest", intersect: true },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            title: (items) => {
              const lbl = items[0]?.label;
              return lbl ? [`${lbl}:`] : [];
            },
            label: (ctx) => {
              const v = ctx.parsed;
              const pct = total ? Math.round((v / total) * 100) : 0;
              return `${formatCount(v)} (${pct}%)`;
            },
          },
        },
      },
    }),
    [total],
  );

  return (
    <div className="pos-customer-group-donut" aria-label={t("graphCustomerGroup", { defaultValue: "Customer group" })}>
      <div className="pos-customer-group-donut__chart">
        <div className="pos-customer-group-donut__canvas">
          <Doughnut data={chartData} options={options} />
        </div>
        <div className="pos-customer-group-donut__center">
          <span className="pos-customer-group-donut__center-label">{t("Customer", { defaultValue: "Customer" })}</span>
          <span className="pos-customer-group-donut__center-value">{formatCount(total)}</span>
        </div>
      </div>
      <CustomerGroupFooterLegend model={model} />
    </div>
  );
}

/**
 * @param {{ model: import("./dashboardChartsModel.js").CustomerGroupModel }} props
 */
/** Y-axis max + tick step so small counts (e.g. 1) are visible — avoid a fixed 0–200 scale. */
function customerGroupBarYScale(maxCount) {
  const m = Number(maxCount);
  const maxC = Number.isFinite(m) ? Math.max(0, m) : 0;
  if (maxC <= 0) {
    return { yMax: 5, tickStep: 1 };
  }
  const headroom = maxC * 1.12;
  let yMax;
  if (headroom <= 10) {
    yMax = Math.max(maxC + 1, Math.ceil(headroom));
  } else if (headroom <= 50) {
    yMax = Math.ceil(headroom / 5) * 5;
  } else if (headroom <= 250) {
    yMax = Math.ceil(headroom / 10) * 10;
  } else {
    yMax = Math.ceil(headroom / 50) * 50;
  }
  const tickStep = yMax <= 10 ? (yMax <= 6 ? 1 : 2) : yMax <= 50 ? 5 : yMax <= 200 ? 10 : 50;
  return { yMax, tickStep };
}

function CustomerGroupBarBody({ model }) {
  const { t } = useTranslation();

  const { total, yMax, tickStep } = useMemo(() => {
    const tiers = model?.tiers ?? [];
    let tot = 0;
    let maxC = 0;
    for (const tier of tiers) {
      const v = Number(tier.count);
      const c = Number.isFinite(v) ? v : 0;
      tot += c;
      maxC = Math.max(maxC, c);
    }
    const { yMax: cap, tickStep: step } = customerGroupBarYScale(maxC);
    return { total: tot, yMax: cap, tickStep: step };
  }, [model]);

  const { chartData, options } = useMemo(() => {
    const tiers = model?.tiers ?? [];
    const xL = [];
    const data = [];
    const bg = [];
    for (const tier of tiers) {
      xL.push(tier.barLabel ?? "");
      const v = Number(tier.count);
      data.push(Number.isFinite(v) ? v : 0);
      bg.push(tier.color ?? "#F9C54A");
    }
    const chart = {
      labels: xL,
      datasets: [
        {
          label: "",
          data,
          backgroundColor: bg,
          maxBarThickness: 64,
          borderRadius: 0,
          borderSkipped: false,
        },
      ],
    };
    const opts = {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      datasets: {
        bar: {
          categoryPercentage: 0.5,
          barPercentage: 0.58,
        },
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            title: (items) => {
              const lbl = items[0]?.label;
              return lbl ? [`${lbl}:`] : [];
            },
            label: (ctx) => formatCount(Number(ctx.parsed?.y)),
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: {
            font: { family: "Roboto, sans-serif", size: 11 },
            color: "#666666",
          },
        },
        y: {
          min: 0,
          max: yMax,
          border: { display: false },
          grid: { color: "#E6E6E6" },
          title: {
            display: true,
            text: t("Person", { defaultValue: "Person" }),
            color: "#666666",
            font: { family: "Roboto, sans-serif", size: 12 },
          },
          ticks: {
            stepSize: tickStep,
            font: { family: "Roboto, sans-serif", size: 11 },
            color: "#666666",
            callback: (v) => formatCount(v),
          },
        },
      },
    };
    return { chartData: chart, options: opts };
  }, [model, t, yMax, tickStep]);

  return (
    <div className="pos-customer-group-bar">
      <div className="pos-customer-group-bar__kpi">
        <span className="pos-customer-group-bar__kpi-label">{t("Customer", { defaultValue: "Customer" })}</span>
        <span className="pos-customer-group-bar__kpi-value">{formatCount(total)}</span>
      </div>
      <div className="pos-customer-group-bar__plot">
        <Bar data={chartData} options={options} />
      </div>
      <CustomerGroupFooterLegend model={model} />
    </div>
  );
}

/**
 * @param {{ model: import("./dashboardChartsModel.js").CustomerGroupModel }} props
 */
function CustomerGroupTableBody({ model }) {
  const { t } = useTranslation();
  const [sort, setSort] = useState(() => ({
    key: /** @type {"group"|"count"|"pct"} */ ("count"),
    dir: /** @type {"asc"|"desc"} */ ("desc"),
  }));

  useEffect(() => {
    setSort({ key: "count", dir: "desc" });
  }, [model]);

  const toggleSort = useCallback((key) => {
    setSort((s) => (s.key !== key ? { key, dir: "asc" } : { key, dir: s.dir === "asc" ? "desc" : "asc" }));
  }, []);

  const baseRows = useMemo(() => {
    const tiers = model?.tiers ?? [];
    let total = 0;
    for (const tier of tiers) {
      const v = Number(tier.count);
      total += Number.isFinite(v) ? v : 0;
    }
    return tiers.map((tier, i) => {
      const c = Number(tier.count);
      const count = Number.isFinite(c) ? c : 0;
      const pct = total > 0 ? Math.round((count / total) * 100) : 0;
      return {
        key: `cg-td-${i}-${tier.id}`,
        tierId: tier.id,
        tableLabel: tier.tableLabel ?? tier.legendLabel,
        count,
        pct,
      };
    });
  }, [model]);

  const rows = useMemo(() => {
    const copy = baseRows.map((r) => ({ ...r }));
    const mul = sort.dir === "asc" ? 1 : -1;
    copy.sort((a, b) => {
      let c = 0;
      switch (sort.key) {
        case "group":
          c = String(a.tableLabel).localeCompare(String(b.tableLabel), undefined, { sensitivity: "base" });
          break;
        case "count":
          c = a.count - b.count;
          break;
        case "pct":
          c = a.pct - b.pct;
          break;
        default:
          c = 0;
      }
      return c * mul;
    });
    return copy;
  }, [baseRows, sort]);

  const thBase = "pos-customer-group-table__th";
  const thSorted = "pos-customer-group-table__th--sorted";

  return (
    <div className="pos-customer-group-table" role="table" aria-label={t("graphCustomerGroup", { defaultValue: "Customer group" })}>
      <div className="pos-customer-group-table__head" role="rowgroup">
        <div className="pos-customer-group-table__tr pos-customer-group-table__tr--head" role="row">
          <DashboardChartTableSortHeader
            baseClassName={thBase}
            sortedClassName={thSorted}
            className="pos-customer-group-table__th--group"
            ariaSortLabel={t("Sort by group", { defaultValue: "Sort by group" })}
            sorted={sort.key === "group"}
            direction={sort.dir}
            onSort={() => toggleSort("group")}
          >
            {t("Group", { defaultValue: "Group" })}
          </DashboardChartTableSortHeader>
          <DashboardChartTableSortHeader
            baseClassName={thBase}
            sortedClassName={thSorted}
            className="pos-customer-group-table__th--customer"
            ariaSortLabel={t("Sort by customer count", { defaultValue: "Sort by customer count" })}
            sorted={sort.key === "count"}
            direction={sort.dir}
            onSort={() => toggleSort("count")}
          >
            {t("Customer", { defaultValue: "Customer" })}
          </DashboardChartTableSortHeader>
          <DashboardChartTableSortHeader
            baseClassName={thBase}
            sortedClassName={thSorted}
            className="pos-customer-group-table__th--pct"
            ariaSortLabel={t("Sort by percent", { defaultValue: "Sort by percent" })}
            sorted={sort.key === "pct"}
            direction={sort.dir}
            onSort={() => toggleSort("pct")}
          >
            {t("%", { defaultValue: "%" })}
          </DashboardChartTableSortHeader>
        </div>
      </div>
      <div className="pos-customer-group-table__scroll" role="rowgroup">
        {rows.map((row) => (
          <div key={row.key} className="pos-customer-group-table__tr" role="row">
            <div className="pos-customer-group-table__td pos-customer-group-table__td--group" role="cell">
              <div className="pos-customer-group-table__group-cell">
                <TierMedal tierId={row.tierId} label={row.tableLabel} />
                <span>{row.tableLabel}</span>
              </div>
            </div>
            <div className="pos-customer-group-table__td pos-customer-group-table__td--customer" role="cell">
              {formatCount(row.count)}
            </div>
            <div className="pos-customer-group-table__td pos-customer-group-table__td--pct" role="cell">
              {row.pct}%
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Customer group — donut / bar / table + kebab (Pie Chart, Chart, Table, Download, Remove).
 *
 * @param {object} props
 * @param {import("./dashboardChartsModel.js").CustomerGroupModel | undefined} [props.model]
 * @param {() => void} [props.onMenuRemove]
 */
function CustomerGroupChartCard({ model, onMenuRemove }) {
  const { t } = useTranslation();
  const captureRef = useRef(/** @type {HTMLDivElement | null} */ (null));
  const [viewKind, setViewKind] = useState(/** @type {"donut"|"bar"|"table"} */ ("donut"));
  const mergedModel = useMemo(() => mergeCustomerGroupModel(model ?? null), [model]);

  const handleDownloadPng = useCallback(async () => {
    await downloadElementAsPng(captureRef.current, buildTimestampedPngFilename("customer-group"));
  }, []);

  return (
    <div
      className={`pos-dashboard-chart-card pos-dashboard-chart-card--customer-group${
        viewKind === "table" ? " pos-dashboard-chart-card--customer-group-table" : ""
      }`}
    >
      <header className="pos-dashboard-chart-card__header pos-dashboard-chart-card__header--customer-group">
        <h2 className="pos-dashboard-chart-card__title">{t("graphCustomerGroup", { defaultValue: "Customer group" })}</h2>
        <div className="pos-dashboard-chart-card__header-actions">
          <SegmentChartCardKebabMenu
            viewKind={viewKind}
            onViewKindChange={setViewKind}
            onDownload={handleDownloadPng}
            onRemove={onMenuRemove}
          />
        </div>
      </header>
      <div
        className={`pos-dashboard-chart-card__body pos-dashboard-chart-card__body--customer-group${
          viewKind === "table" ? " pos-dashboard-chart-card__body--customer-group-table" : ""
        }`}
      >
        <div ref={captureRef} className="pos-dashboard-chart-card__capture pos-dashboard-chart-card__capture--customer-group">
          {viewKind === "donut" ? <CustomerGroupDonutBody model={mergedModel} /> : null}
          {viewKind === "bar" ? <CustomerGroupBarBody model={mergedModel} /> : null}
          {viewKind === "table" ? <CustomerGroupTableBody model={mergedModel} /> : null}
        </div>
      </div>
    </div>
  );
}

export default CustomerGroupChartCard;
